import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EMPTY, Observable, Subject, of, forkJoin } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from 'services/api.service';
import { ClientCard } from 'types/client.interface';
import { LegalEntityListItem } from 'types/legal-entity.interface';

const SEARCH_DEBOUNCE_TIME = 500;
const BLUR_TIMEOUT = 150;

interface SearchResult {
  id: string;
  displayName: string;
  displayId: string;
  isLegalEntity: boolean;
  legalEntityId?: string;
  clientCard?: ClientCard;
}

@Component({
  selector: 'app-search-client-input',
  standalone: true,
  imports: [FormsModule, NgClass],
  templateUrl: './search-client-input.component.html',
  styleUrl: './search-client-input.component.scss'
})
export class SearchClientInputComponent implements OnChanges {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') dropdownRef!: ElementRef<HTMLDivElement>;
  @Input() invalid: boolean = false;
  @Input() clientType?: number;
  @Output() clientSelected: EventEmitter<ClientCard | null> = new EventEmitter<ClientCard | null>();
  private searchSubject: Subject<string> = new Subject<string>();
  value: string = '';
  clients: ClientCard[] = [];
  searchResults: SearchResult[] = [];
  isActive: boolean = false;
  isLoading: boolean = false;

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideInput = this.searchInput?.nativeElement.contains(target);
    const clickedInsideDropdown = !!this.dropdownRef?.nativeElement && this.dropdownRef.nativeElement.contains(target);
    if (!clickedInsideInput && !clickedInsideDropdown) {
      this.isActive = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientType'] && !changes['clientType'].firstChange) {
      this.clearSearch();
    }
  }

  constructor(private apiService: ApiService) {
    this.searchSubject
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        tap(() => {
          this.isLoading = true;
          this.isActive = true;
        }),
        map((q) => q.trim()),
        switchMap((q) => {
          if (q.length < 3) {
            this.clients = [];
            this.searchResults = [];
            this.isLoading = false;
            return EMPTY;
          }
          if (this.clientType === 2) {
            return this.searchLegalEntities(q);
          }
          return this.searchFpClients(q);
        })
      )
      .subscribe((list) => {
        if (this.clientType === 2) {
          this.searchResults = list as SearchResult[];
          this.clients = [];
        } else {
          this.clients = list as ClientCard[];
          this.searchResults = [];
        }
        this.isLoading = false;
        const hasResults = this.clientType === 2 ? this.searchResults.length > 0 : this.clients.length > 0;
        this.isActive = hasResults && document.activeElement === this.searchInput?.nativeElement;
      });
  }

  private searchFpClients(query: string): Observable<ClientCard[]> {
    return forkJoin([
      this.apiService.getClientsForInput(query, 'unfinished', this.clientType),
      this.apiService.getClientsForInput(query, 'active', this.clientType)
    ]).pipe(
      map(([unfinished, active]) => [...(unfinished.list ?? []), ...(active.list ?? [])] as ClientCard[]),
      catchError(() => of([]))
    );
  }

  private searchLegalEntities(query: string): Observable<SearchResult[]> {
    return this.apiService.getLegalEntitiesForInput(query).pipe(
      map((response) =>
        (response.list ?? []).map((le: LegalEntityListItem) => ({
          id: le.id,
          displayName: le.name,
          displayId: le.registrationNumber,
          isLegalEntity: true,
          legalEntityId: le.id
        }))
      ),
      catchError(() => of([]))
    );
  }

  onChange(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.searchSubject.next(raw);
  }

  clearSearch(): void {
    this.value = '';
    this.clients = [];
    this.searchResults = [];
    this.clientSelected.emit(null);
    this.searchSubject.next('');
    this.isActive = false;
  }

  getClients(): void {
    this.isLoading = true;
    if (this.clientType === 2) {
      this.searchLegalEntities(this.value).subscribe((list) => {
        this.searchResults = list;
        this.isLoading = false;
      });
    } else {
      this.searchFpClients(this.value).subscribe((list) => {
        this.clients = list;
        this.isLoading = false;
      });
    }
  }

  onFocus(): void {
    this.invalid = false;
    this.isActive = true;
  }

  onBlur(): void {
    setTimeout(() => {
      this.isActive = false;
    }, BLUR_TIMEOUT);
  }

  selectClient(client: ClientCard): void {
    this.clientSelected.emit(client);
    this.value = `${client.fullName} (${client.officialDocumentId ?? '-'})`;
    this.isActive = false;
    this.searchInput.nativeElement.blur();
  }

  selectLegalEntity(result: SearchResult): void {
    this.isLoading = true;
    this.isActive = false;
    this.value = `${result.displayName} (${result.displayId})`;

    this.apiService.getLegalEntity(result.legalEntityId!).subscribe({
      next: (legalEntity) => {
        const pseudoClient: ClientCard = {
          id: legalEntity.id,
          legalEntityId: legalEntity.id,
          legalEntityName: legalEntity.name,
          fullName: legalEntity.name,
          nationalDigitalId: legalEntity.registrationNumber,
          email: legalEntity.email ?? undefined,
          phone: legalEntity.phone ?? undefined,
          address: legalEntity.legalAddress ?? undefined,
          authenticationType: legalEntity.authenticationType
        } as ClientCard;
        this.clientSelected.emit(pseudoClient);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.clientSelected.emit(null);
      }
    });
  }
}
